'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

type DirectoryIcon = 'gavel' | 'building' | 'calculator' | 'graduation-cap';

type ProfessionalDirectoryCardProps = {
  title: string;
  description: string;
  href: string;
  icon: DirectoryIcon;
  countLabel?: string;
};

const badgeImageMap: Record<DirectoryIcon, string> = {
  gavel: '/images/legal.webp',
  building: '/images/realestate.webp',
  calculator: '/images/finance.webp',
  'graduation-cap': '/images/education.webp',
};

// All cards share the same brand token system — differentiated by opacity only
const accentMap: Record<DirectoryIcon, string> = {
  gavel:           'border-primary/25',
  building:        'border-primary/20',
  calculator:      'border-primary/15',
  'graduation-cap':'border-primary/20',
};

export default function ProfessionalDirectoryCard({
  title,
  description,
  href,
  icon,
  countLabel,
}: ProfessionalDirectoryCardProps) {
  const badgeSrc = badgeImageMap[icon];

  return (
    <Link
      href={href}
      aria-label={`Explore ${title} AI directory`}
      className="group block h-full"
    >
      <div
        className={`
          relative flex flex-row items-start sm:flex-col h-full gap-4 sm:gap-0
          rounded-2xl border bg-card/60 backdrop-blur-sm p-4 sm:p-5
          transition-all duration-300
          hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:scale-[1.02] hover:border-primary/40
          ${accentMap[icon]}
        `}
      >
        {/* Icon (Left on mobile, Top on desktop) */}
        <div className="flex flex-row items-center sm:items-start justify-between sm:mb-5 gap-3 sm:gap-0 shrink-0">
          <div className="relative group/badge flex-shrink-0">
            {/* Single brand-consistent glow behind badge */}
            <div className="absolute -inset-1.5 rounded-full bg-primary/15 opacity-60 blur-md transition-opacity duration-300 group-hover:opacity-100" />

            {/* Badge container */}
            <div className="relative h-12 w-12 sm:h-16 sm:w-16 rounded-full border border-border/40 bg-card/50 p-0.5 backdrop-blur-sm shadow-[0_4px_20px_rgba(0,0,0,0.15)] transition-transform duration-300 group-hover/badge:scale-105">
              <Image
                src={badgeSrc}
                alt={`${title} Directory Certification Seal`}
                width={64}
                height={64}
                className="h-full w-full object-contain rounded-full"
              />
            </div>
          </div>
          {/* Badge hidden on mobile (moved to text column), shown on desktop */}
          {countLabel && (
            <Badge className="hidden sm:inline-flex text-[9px] font-black uppercase tracking-widest bg-primary/10 text-primary border-primary/20 px-2 py-0.5 rounded-full text-right shrink-0 shadow-sm mt-1">
              {countLabel}
            </Badge>
          )}
        </div>

        {/* Text content (Right on mobile, Bottom on desktop) */}
        <div className="flex flex-col flex-1 min-w-0 justify-center sm:justify-start">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-base font-bold tracking-tight text-foreground flex items-center gap-1.5 group-hover:text-primary transition-colors">
              {title}
              <ChevronRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
            </h3>
            {/* Badge shown on mobile only */}
            {countLabel && (
              <Badge className="sm:hidden text-[8px] font-black uppercase tracking-widest bg-primary/10 text-primary border-primary/20 px-2 py-0.5 rounded-full shrink-0 shadow-sm">
                {countLabel}
              </Badge>
            )}
          </div>

          <p className="text-[0.75rem] leading-relaxed text-muted-foreground line-clamp-2 sm:line-clamp-3">
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}
